import type { ReactElement } from 'react';

import { cn } from '@/components/ui/cn';
import { stageKindLabel } from '@/lib/stage-kind';
import { ExportMenu } from '@/components/ui/ExportMenu';
import { CardFrame, CardNote, QueryBody, RANGE_NOTE } from '@/components/ui/card-frame';

import {
  analysisQuery,
  deviationTone,
  formatHours,
  formatPct,
  rangeProblem,
  realOf,
} from '../presentation';
import type { DeviationRow, TimeStudyFilters } from '../types';
import { useDeviation } from '../use-time-study';

// Estándar frente a real por etapa (so=420, spec §4.2 tarjeta 3; mockup 05): barra del
// esfuerzo real con la marca del estándar y la desviación, en el orden del flujo. G4: la
// capacitación va al final, por calendario y con su propia escala (en días aplastaría las
// barras de horas de trabajo).

const ORDER = ['elaboracion', 'revision', 'aprobacion'] as const;

interface Props {
  filters: TimeStudyFilters;
  companyId: string;
}

function StageRow({ row, scale }: { row: DeviationRow; scale: number }): ReactElement {
  const calendar = row.basis === 'calendar';
  const stage = row.keys.stage ?? 'elaboracion';
  const tone = deviationTone(row.deviationPct);
  const pct = (s: number): string => `${Math.min(100, (s / scale) * 100)}%`;
  return (
    <li className="brow">
      <div className="lbl">
        {stageKindLabel(stage)}
        <div className="std">
          {row.standardSeconds === null
            ? 'sin estándar'
            : `estándar ${formatHours(row.standardSeconds)}`}
        </div>
        {calendar && <div className="std">tiempo de calendario</div>}
      </div>
      <div className="range" aria-hidden="true">
        <div className={cn('fill', tone)} style={{ width: pct(realOf(row)) }} />
        {row.standardSeconds !== null && (
          <div className="mark" style={{ left: pct(row.standardSeconds) }} />
        )}
      </div>
      <div className={cn('dv', tone)}>
        {formatHours(realOf(row))} · {formatPct(row.deviationPct)}
      </div>
    </li>
  );
}

/** Filas de etapa en el orden del flujo, a una escala común (esfuerzo real o estándar,
 *  lo que sea mayor). La usan Estudio de tiempos y el Panel del QMS. */
const scaleOf = (rows: DeviationRow[]): number =>
  Math.max(1, ...rows.map((r) => Math.max(realOf(r), r.standardSeconds ?? 0)));

export function StageBars({ rows }: { rows: DeviationRow[] }): ReactElement {
  const ordered = ORDER.flatMap((s) => rows.filter((r) => r.keys.stage === s && !r.basis));
  const calendar = rows.filter((r) => r.basis === 'calendar');
  const scale = scaleOf(ordered);
  return (
    <ul className="cb bars">
      {ordered.map((r) => (
        <StageRow key={r.keys.stage} row={r} scale={scale} />
      ))}
      {calendar.map((r) => (
        <StageRow key={`cal-${r.keys.stage ?? ''}`} row={r} scale={scaleOf([r])} />
      ))}
    </ul>
  );
}

export function StageVsStandardCard({ filters, companyId }: Props): ReactElement {
  const problem = rangeProblem(filters);
  const query = useDeviation('stage', filters, { companyId, enabled: problem === null });
  return (
    <CardFrame
      title="Estándar frente a real por etapa"
      count="esfuerzo real · estándar vigente"
      actions={
        problem === null && (
          <ExportMenu
            compact
            path="/api/v1/quality/productivity/deviation/export"
            query={`groupBy=stage&${analysisQuery(filters)}`}
            title="Estándar frente a real por etapa"
          />
        )
      }
    >
      {problem !== null ? (
        <CardNote>{RANGE_NOTE}</CardNote>
      ) : (
        <QueryBody
          query={query}
          isEmpty={(d) => d.rows.length === 0}
          emptyText="No hay trabajos terminados en el rango."
        >
          {(d) => <StageBars rows={d.rows} />}
        </QueryBody>
      )}
    </CardFrame>
  );
}
